/**
 * The one reading of "is this session over?" (R11) — shared by the input
 * gate, the clause walk, and the store's selectors, so a terminal ending
 * cannot mean two things in two places.
 *
 * @remarks
 * `narrative.selectedEndingIds` is the state's whole answer; the KIND is the
 * ending definition's. `'open'` endings conclude the story but never close
 * the world — and, since D151, never spend the slot either — so only a
 * `'terminal'` selection gates anything.
 *
 * @packageDocumentation
 */

import type { EndingDefinition, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

/**
 * Every ending selected this session, in selection order (D151) — the
 * history the journal and the Voices surfaces read: masked, AND THEN died.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The endings, oldest first (empty until one is selected)
 */
export const selectedEndingsOf = (bundle: EngineGameBundle, state: RuntimeState): readonly EndingDefinition[] =>
	state.narrative.selectedEndingIds.flatMap((endingId) => {
		const ending = bundle.indexes.endingsById.get(endingId);

		return ending === undefined ? [] : [ending];
	});

/**
 * The ending most recently selected — where the story IS now, for a banner.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The latest ending, if any
 */
export const selectedEndingOf = (bundle: EngineGameBundle, state: RuntimeState): EndingDefinition | undefined =>
	selectedEndingsOf(bundle, state).at(-1);

/**
 * The terminal ending, if the world has been closed by one. A terminal ending
 * consumes the slot (D151), so it is always the last selected — the latest IS
 * the terminal one when there is one.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The terminal ending, or nothing while the world is open
 */
export const terminalEndingOf = (bundle: EngineGameBundle, state: RuntimeState): EndingDefinition | undefined => {
	const ending = selectedEndingOf(bundle, state);

	return ending?.kind === 'terminal' ? ending : undefined;
};
